import { TableCell, TableRow } from "@loot-os/ui/components/table";
import { cn } from "@loot-os/ui/lib/utils";
import { ChevronRight } from "lucide-react";
import type { MouseEvent, ReactNode } from "react";

// The open row and the detail under it share one edge in the primary color, so the pair reads
// as a single block and the eye finds which row the fields belong to.
const OPEN_EDGE = "shadow-[inset_2px_0_0_var(--primary)]";

// What a click inside the row belongs to rather than to the row: a link, a copy or retry button.
const INTERACTIVE = "a, button, input, select, textarea, label";

/**
 * A table row that opens in place, the analytics event log's pattern: a chevron cell leads, a
 * click anywhere on the row that is not on a link or button of its own toggles it, and while
 * open the row keeps a solid fill, loses its bottom border and has `detail` under it at the
 * card's width. The table's `CardContent` must be an `@container` for that width.
 */
export function ExpandableRow({
  detailId,
  label,
  isOpen,
  onToggle,
  colSpan,
  children,
  detail,
}: {
  /** The detail row's DOM id, which the chevron names in `aria-controls`. */
  detailId: string;
  /** What the chevron says to a screen reader, "Show order #1042" and the like. */
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  /** Every column of the table, the chevron's included. */
  colSpan: number;
  /** The row's cells after the chevron. */
  children: ReactNode;
  /** Rendered only while open. */
  detail: ReactNode;
}) {
  const onRowClick = (e: MouseEvent<HTMLTableRowElement>) => {
    const target = e.target as Element;
    // A portalled popover's click bubbles here through React but is not inside the row.
    if (!e.currentTarget.contains(target) || target.closest(INTERACTIVE)) return;
    onToggle();
  };
  return (
    <>
      <TableRow
        className={cn("cursor-pointer", isOpen && "border-b-0 bg-muted hover:bg-muted")}
        onClick={onRowClick}
      >
        <TableCell className={cn("w-8 pr-0", isOpen && OPEN_EDGE)}>
          <button
            type="button"
            aria-expanded={isOpen}
            aria-controls={detailId}
            aria-label={label}
            className="flex cursor-pointer items-center rounded-sm text-muted-foreground outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
            onClick={onToggle}
          >
            <ChevronRight
              className={cn("size-3.5 transition-transform", isOpen && "rotate-90 text-primary")}
            />
          </button>
        </TableCell>
        {children}
      </TableRow>
      {isOpen && (
        <TableRow id={detailId} className="bg-muted/40 hover:bg-muted/40">
          <TableCell colSpan={colSpan} className={cn("whitespace-normal", OPEN_EDGE)}>
            {/* As wide as the card and held at its left edge: on a window too narrow for the
                columns the table scrolls sideways, and the detail must not scroll off with it. */}
            <div className="sticky left-(--card-spacing) w-[100cqw] max-w-full">{detail}</div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
